import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchLogins } from '../support/useLogins';

/**
 * Returns a `prefetch(path)` function that pre-warms React Query caches
 * for the primary list of the module matching the given path prefix.
 *
 * Designed for use with onMouseEnter on nav links so data is ready by the
 * time the user's click is processed (~200ms head start).
 */
export function useNavPrefetch(orgId: string | undefined) {
  const queryClient = useQueryClient();

  const prefetch = useCallback(
    (path: string) => {
      if (!orgId) return;

      // No customer or quote list prefetch (#6991): the keys they filled were read by no screen (the Customers page reads
      // its own paginated list, and the quote lists key on their date filters), so they cost a read per hover for nothing.

      // Support > Logins. Worth prefetching more than most: it is served by a
      // Cloud Function that is almost always cold, so the head start here is
      // measured in seconds rather than the usual ~200ms.
      if (path.startsWith('/support/logins')) {
        prefetchLogins(queryClient, orgId);
        return;
      }

      // No project or lead list prefetch (#6991 review): their list hooks build rows in their own shape (a lead's
      // leadId, archived leads left out, every project), and a key filled here would stand in for that list.
    },
    [orgId, queryClient],
  );

  return { prefetch };
}
